import React from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts/highstock'
import HighchartsReact from 'highcharts-react-official'
import HighchartsExporting from 'highcharts/modules/exporting'
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting'
import { createDataLabelFormatter } from '../helpers/chart-formatters'
import {
  XAxisColumnOption,
  DateGroupingOption,
  YAxisColumnOption,
  YAxisAggregationOption,
  YAxisLabelOption,
  YAxisGroupByOption,
  DisplayLimitOption
} from './chart-options'
import { drillDownPlotOptions } from './drilldown'

HighchartsExporting(Highcharts)
HighchartsOfflineExporting(Highcharts)

function VerticalBarChart({ categories, series, title, tooltipFormatter, yAxisTitle, column, hasGrouping = false, colors = null, chartRef = null, onPointClick = null }) {
  const normalizedSeries = series.map((value) =>
    typeof value === 'number' ? { y: value } : value
  )

  // Handle grouped data for vertical bar charts with percentage breakdown
  // Filter out "Total" series if present (it's added by backend but shouldn't be shown for stacked bar charts)
  let chartSeries = hasGrouping ? series : [{
    name: 'Data',
    data: normalizedSeries,
    showInLegend: false
  }]

  if (hasGrouping && chartSeries.length > 0 && chartSeries[0]?.name === 'Total') {
    chartSeries = chartSeries.slice(1) // Remove the first series (Total)
  }

  // Get data label formatter from shared helper
  const dataLabelFormatter = createDataLabelFormatter({ column })

  const options = {
    ...(colors && { colors }), // Apply brand colors if provided
    chart: {
      type: 'column',
      zoomType: 'x'
    },
    title: {
      text: title || ''
    },
    credits: {
      enabled: false
    },
    exporting: {
      enabled: true,
      filename: title || 'chart',
      fallbackToExportServer: false,
      libURL: '', // Prevent loading any external libraries
      buttons: {
        contextButton: {
          enabled: false
        }
      }
    },
    xAxis: {
      categories,
      title: {
        text: ''
      }
    },
    yAxis: {
      min: 0,
      title: {
        text: yAxisTitle || 'Count (n)'
      },
      labels: {
        overflow: 'justify'
      },
      allowDecimals: false
    },
    tooltip: {
      shared: true,
      formatter: tooltipFormatter
    },
    plotOptions: {
      ...drillDownPlotOptions(onPointClick),
      column: {
        stacking: hasGrouping ? 'normal' : null,
        dataLabels: {
          enabled: !hasGrouping, // Disable data labels for grouped charts to avoid clutter
          formatter: dataLabelFormatter
        }
      }
    },
    legend: {
      enabled: hasGrouping
    },
    series: chartSeries
  }

  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} updateArgs={[true, true, false]} />
}

VerticalBarChart.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  series: PropTypes.array.isRequired,
  title: PropTypes.string,
  tooltipFormatter: PropTypes.func,
  yAxisTitle: PropTypes.string,
  column: PropTypes.object,
  hasGrouping: PropTypes.bool,
  colors: PropTypes.arrayOf(PropTypes.string),
  chartRef: PropTypes.object,
  onPointClick: PropTypes.func
}

// Chart configuration for vertical bar charts
export const verticalBarChartConfig = {
  requiredFields: ['title', 'xAxisColumn', 'yAxisColumn'],

  validate: (formData) => {
    const errors = {}
    if (!formData.xAxisColumn) {
      errors.xAxisColumn = 'X-Axis column is required for bar charts'
    }
    if (!formData.yAxisColumn) {
      errors.yAxisColumn = 'Y-Axis column is required for bar charts'
    }
    return errors
  },

  isQueryEnabled: (formData, hasChartId) => {
    return !!(hasChartId && formData.xAxisColumn && formData.yAxisColumn)
  },

  buildChartParams: (formData) => ({
    group_by_column_id: formData.groupBy,
    x_axis_column_id: formData.xAxisColumn,
    x_axis_date_grouping: formData.xAxisDateGrouping,
    y_axis_column_id: formData.yAxisColumn,
    y_axis_aggregation: formData.yAxisAggregation,
    y_axis_label: formData.yAxisLabel,
    y_axis_group_by_column_id: formData.yAxisGroupBy,
    display_limit: formData.displayLimit === '' || formData.displayLimit == null
      ? null
      : formData.displayLimit
  }),

  // Build query params for API request
  buildQueryParams: (baseParams, formData, columns) => {
    const xAxisCol = columns?.find((c) => c.id === formData.xAxisColumn)
    const params = { ...baseParams }
    params.group_by = xAxisCol?.database_column_name
    if (formData.xAxisDateGrouping && xAxisCol?.display_as === 'date') {
      params.date_grouping = formData.xAxisDateGrouping
    }
    if (formData.yAxisColumn) {
      const yAxisCol = columns?.find((c) => c.id === formData.yAxisColumn)
      params.y_axis_column = yAxisCol?.database_column_name
      params.y_axis_aggregation = formData.yAxisAggregation || 'count'
    }
    const yAxisGroupByCol = columns?.find((c) => c.id === formData.yAxisGroupBy)
    params.y_axis_group_by = yAxisGroupByCol?.database_column_name
    if (formData.displayLimit !== '' && formData.displayLimit != null) {
      params.display_limit = formData.displayLimit
    }
    return params
  },

  // Supports date formatting
  supportsDateFormatting: true,

  // Render chart-specific options in the modal form
  renderOptions: ({
    Form,
    formState,
    setters,
    validationErrors,
    hasSubmitted,
    columns,
    getGroupingColumns,
    getValueColumns,
    getAggregationOptions,
    getColumnDisplayName,
    getAggregationLabel,
    municipality
  }) => {
    const {
      xAxisColumn,
      xAxisDateGrouping,
      yAxisColumn,
      yAxisAggregation,
      yAxisLabel,
      yAxisGroupBy,
      displayLimit
    } = formState

    const {
      setXAxisColumn,
      setXAxisDateGrouping,
      setYAxisColumn,
      setYAxisAggregation,
      setYAxisLabel,
      setValidationErrors,
      setYAxisGroupBy,
      setDisplayLimit
    } = setters

    return (
      <div className="mb-3">
        <XAxisColumnOption
          Form={Form}
          xAxisColumn={xAxisColumn}
          setXAxisColumn={setXAxisColumn}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          hasSubmitted={hasSubmitted}
          getGroupingColumns={getGroupingColumns}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
        />

        <DateGroupingOption
          Form={Form}
          xAxisColumn={xAxisColumn}
          xAxisDateGrouping={xAxisDateGrouping}
          setXAxisDateGrouping={setXAxisDateGrouping}
          columns={columns}
        />

        <YAxisColumnOption
          Form={Form}
          yAxisColumn={yAxisColumn}
          setYAxisColumn={setYAxisColumn}
          yAxisAggregation={yAxisAggregation}
          setYAxisAggregation={setYAxisAggregation}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          hasSubmitted={hasSubmitted}
          getValueColumns={getValueColumns}
          getAggregationOptions={getAggregationOptions}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
        />

        <YAxisAggregationOption
          Form={Form}
          yAxisAggregation={yAxisAggregation}
          setYAxisAggregation={setYAxisAggregation}
          yAxisColumn={yAxisColumn}
          getAggregationOptions={getAggregationOptions}
          getAggregationLabel={getAggregationLabel}
        />

        <YAxisLabelOption
          Form={Form}
          yAxisLabel={yAxisLabel}
          setYAxisLabel={setYAxisLabel}
        />

         <YAxisGroupByOption
          Form={Form}
          yAxisGroupBy={yAxisGroupBy}
          setYAxisGroupBy={setYAxisGroupBy}
          getGroupingColumns={getGroupingColumns}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
        />

        <DisplayLimitOption
          Form={Form}
          displayLimit={displayLimit}
          setDisplayLimit={setDisplayLimit}
        />
      </div>
    )
  }
}

export default React.memo(VerticalBarChart)
